// ke-email-popup — site-wide signup popup: email -> optional phone -> thank-you with copyable code.
// Display rules: closing hides it for N days (localStorage), subscribing hides it permanently,
// test mode ignores both. In the theme editor it always shows the step chosen in section settings.
class KeEmailPopup extends HTMLElement {
  connectedCallback() {
    this.delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
    this.dismissDays = parseInt(this.dataset.dismissDays, 10) || 14;
    this.testMode = this.dataset.testMode === 'true';
    this.editorStep = this.dataset.editorStep || 'email';
    this.disableInEditor = this.dataset.disableInEditor === 'true';
    this.KEY_DISMISSED = 'ke-popup-dismissed-at';
    this.KEY_SUBSCRIBED = 'ke-popup-subscribed';
    this.designMode = !!(window.Shopify && window.Shopify.designMode);

    this.KEY_PENDING = 'ke-popup-pending-thanks';
    let pendingThanks = false;
    try { pendingThanks = localStorage.getItem(this.KEY_PENDING) === '1'; } catch (e) { /* noop */ }

    if (this.designMode) {
      if (!this.disableInEditor) this.open(this.editorStep);
      this.onSectionSelect = (e) => { if (e.target && e.target.contains(this)) this.open(this.editorStep); };
      document.addEventListener('shopify:section:select', this.onSectionSelect);
    } else if (pendingThanks) {
      // Visitor just came back from the native signup POST (possibly via Shopify's captcha page):
      // greet them with the thank-you + code step.
      try { localStorage.removeItem(this.KEY_PENDING); } catch (e) { /* noop */ }
      this.open('thanks');
    } else if (this.shouldShow()) {
      this.showTimer = setTimeout(() => this.open('email'), this.delay);
    }

    this.querySelectorAll('[data-ke-popup-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.onKeydown = (e) => { if (e.key === 'Escape' && !this.hidden) this.close(); };
    document.addEventListener('keydown', this.onKeydown);

    const form = this.querySelector('form');
    const nextBtn = this.querySelector('[data-ke-popup-next]');
    if (form && nextBtn) {
      nextBtn.addEventListener('click', () => {
        const email = form.querySelector('input[type="email"]');
        const consent = form.querySelector('input[type="checkbox"]');
        if (email && !email.reportValidity()) return;
        if (consent && !consent.reportValidity()) return;
        if (this.querySelector('[data-step="phone"]')) this.showStep('phone');
        else form.requestSubmit();
      });
      // Native submit is about to navigate (and may pass through Shopify's captcha page):
      // remember to show the thank-you step when the visitor lands back here.
      form.addEventListener('submit', () => {
        this.markSubscribed();
        try { localStorage.setItem(this.KEY_PENDING, '1'); } catch (e) { /* noop */ }
      });
      // Phone submit + skip are type="button" on purpose: the theme's inline
      // "Global Button Styling Overrides" repaints every button[type=submit] with !important.
      const submitBtn = this.querySelector('[data-ke-popup-submit]');
      if (submitBtn) submitBtn.addEventListener('click', () => form.requestSubmit());
      const skip = this.querySelector('[data-ke-popup-skip]');
      if (skip) {
        skip.addEventListener('click', () => {
          const phone = form.querySelector('input[type="tel"]');
          if (phone) phone.value = '';
          form.requestSubmit();
        });
      }
    }

    const copy = this.querySelector('[data-ke-popup-copy]');
    if (copy) copy.addEventListener('click', () => this.copyCode(copy));
  }

  disconnectedCallback() {
    clearTimeout(this.showTimer);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.onSectionSelect) document.removeEventListener('shopify:section:select', this.onSectionSelect);
  }

  shouldShow() {
    if (this.testMode) return true;
    try {
      if (localStorage.getItem(this.KEY_SUBSCRIBED)) return false;
      const dismissedAt = parseInt(localStorage.getItem(this.KEY_DISMISSED), 10);
      if (dismissedAt && Date.now() - dismissedAt < this.dismissDays * 24 * 3600 * 1000) return false;
    } catch (e) { /* storage blocked — show */ }
    return true;
  }

  markDismissed() {
    try { localStorage.setItem(this.KEY_DISMISSED, String(Date.now())); } catch (e) { /* noop */ }
  }

  markSubscribed() {
    try { localStorage.setItem(this.KEY_SUBSCRIBED, '1'); } catch (e) { /* noop */ }
  }

  open(step) {
    this.hidden = false;
    this.showStep(step || 'email');
    if ((step || 'email') === 'email' && !this.designMode) {
      const firstInput = this.querySelector('[data-step="email"] input[type="email"]');
      if (firstInput) firstInput.focus();
    }
  }

  close() {
    this.hidden = true;
    if (!this.designMode) this.markDismissed();
  }

  showStep(name) {
    let found = false;
    this.querySelectorAll('.ke-email-popup__step').forEach((s) => {
      const match = s.dataset.step === name;
      s.hidden = !match;
      if (match) found = true;
    });
    // Editor preview may point at the phone step while "Ask for phone" is off — fall back sensibly
    if (!found) this.querySelectorAll('.ke-email-popup__step').forEach((s, i) => { s.hidden = i !== 0; });
  }

  copyCode(button) {
    const code = button.dataset.code;
    const codeEl = this.querySelector('.ke-email-popup__code');
    const success = () => {
      button.classList.add('is-copied');
      const original = button.dataset.originalLabel || (button.dataset.originalLabel = button.textContent);
      button.textContent = '✓ COPIED';
      setTimeout(() => { button.classList.remove('is-copied'); button.textContent = original; }, 1800);
    };
    const selectCode = () => {
      if (!codeEl || !window.getSelection) return;
      const range = document.createRange();
      range.selectNodeContents(codeEl);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    };
    const fallback = () => {
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, code.length);
      let ok = false;
      try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
      document.body.removeChild(ta);
      if (ok) success();
      else selectCode(); // last resort: highlight the code so the visitor can Ctrl/Cmd+C
    };
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(success).catch(fallback);
    } else {
      fallback();
    }
  }

}
customElements.define('ke-email-popup', KeEmailPopup);
